import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { IconRegistryService } from '@rd/core/services/icon-registry/icon-registry.service';
import {
  CashBackMembership,
  CashBackToAccountMembership,
  Enrollment,
  HttpError,
  LoyaltyProgram,
  LoyaltyProgramCategory,
  Membership,
  MembershipType,
} from '@rd/types';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { RedeemCashSettingService } from '../../services/redeem-cash-setting/redeem-cash-setting.service';
import { LoadEnrollments } from '../../store/actions/enrollment.actions';
import {
  ClearCreateAndUpdateMembershipErrors,
  CreateMembership,
  UpdateMembership,
} from '../../store/actions/membership.actions';
import { RedeemCashV2State } from '../../store/reducers';
import {
  getEnrollmentEnrollments,
  getEnrollmentError,
  getEnrollmentLoading,
} from '../../store/selectors/enrollment.selectors';
import {
  getCreateOrUpdateMembershipError,
  getCreateOrUpdateMembershipIsLoading,
  getMembershipLoading,
  getMembershipMemberships,
} from '../../store/selectors/membership.selectors';
import {
  getPreferredRedeemCashLoyaltyProgram,
  getRedeemCashMembership,
} from '../../store/selectors/redeem-cash-combined.selectors';
import {
  getFetchRedeemCashLoyaltyProgramLoading,
  getRedeemCashLoyaltyProgramByCategory,
} from '../../store/selectors/redeem-cash-loyalty-program.selectors';
import {
  RedeemCashV2LinkAccountDetailFormData,
  RedeemCashV2LinkAccountFormData,
} from '../../types';

type ComponentRenderState =
  | 'loading'
  | 'content'
  | 'empty'
  | 'submitting'
  | 'error';

interface MembershipAndLoyaltyProgramData {
  loyaltyProgram: LoyaltyProgram;
  membership: Partial<CashBackMembership> | null;
}

@Component({
  selector: 'rd-rcv2-checkout-link-account-dialog',
  templateUrl: './checkout-link-account-dialog.component.html',
  styleUrls: ['./checkout-link-account-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RcV2CheckoutLinkAccountDialogComponent
  implements OnInit, OnDestroy
{
  @HostBinding('class.rd-rcv2-checkout-link-account-dialog')
  componentClass = true;
  private destroy$ = new Subject();

  enrollments: Partial<Enrollment>[];
  enrollmentsLoading: boolean;
  enrollmentsError: HttpError | null;
  isSubmitted$: Subject<boolean> = new Subject();
  preferredRedeemCashLoyaltyProgram: LoyaltyProgram | null;
  redeemCashMembership: CashBackMembership | null;
  createOrUpdateMembershipIsLoading: boolean;
  createOrUpdateMembershipError: HttpError | null;

  formData: RedeemCashV2LinkAccountFormData;
  formDetailData: RedeemCashV2LinkAccountDetailFormData;
  linkAccountFormGroup: UntypedFormGroup;
  componentRenderState: ComponentRenderState;
  isBankAccountFormActive = false;
  shouldAllowRedeemToOtherAccount: boolean;
  membershipAndRedeemCashByCashToAccount$: Observable<MembershipAndLoyaltyProgramData>;
  membershipAndRedeemCashByCashToCard$: Observable<MembershipAndLoyaltyProgramData>;

  constructor(
    private cdRef: ChangeDetectorRef,
    private fb: UntypedFormBuilder,
    private iconRegistryService: IconRegistryService,
    private store: Store<RedeemCashV2State>,
    private redeemCashSettingService: RedeemCashSettingService,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(new LoadEnrollments());
    this.shouldAllowRedeemToOtherAccount =
      this.redeemCashSettingService.shouldAllowRedeemToOtherAccount;
    this.linkAccountFormGroup = this.fb.group({
      linkAccountForm: [''],
      linkOtherBankAccountForm: [''],
    });

    // Subscribe to already loaded redeem cash loyalty program and memberships

    this.store
      .select(getPreferredRedeemCashLoyaltyProgram)
      .pipe(takeUntil(this.destroy$))
      .subscribe((preferredRedeemCashLoyaltyProgram) => {
        this.preferredRedeemCashLoyaltyProgram =
          preferredRedeemCashLoyaltyProgram;
        this.checkShouldEnableAccountDetailForm();
      });

    this.store
      .select(getRedeemCashMembership)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (redeemCashMembership) =>
          (this.redeemCashMembership = redeemCashMembership),
      );

    combineLatest([
      this.store.select(getEnrollmentEnrollments),
      this.store.select(getEnrollmentLoading),
      this.store.select(
        getRedeemCashLoyaltyProgramByCategory(
          LoyaltyProgramCategory.CashbackToAccount,
        ),
      ),
      this.store.select(getMembershipMemberships),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        ([enrollments, isEnrollmentLoading, loyaltyProgram, memberships]) => {
          // when the Enrollment is still loading, we will skip the action
          if (isEnrollmentLoading) {
            return;
          }

          this.enrollments = Object.values(enrollments).filter(
            (enrollment) =>
              !this.redeemCashSettingService
                .shouldFilterSupplementaryCardHolder ||
              enrollment.cardholderType !== 'supplementary',
          );

          if (
            this.enrollments.length === 0 &&
            !this.redeemCashSettingService.shouldAllowRedeemToOtherAccount
          ) {
            this.toggleComponentRenderState('empty');
          } else {
            // fetching current enrollment
            if (this.enrollments.length > 0) {
              if (this.redeemCashMembership) {
                const linkedEnrollment = this.enrollments.find((enrollment) => {
                  return (
                    enrollment.groupReferenceId ===
                    // @ts-ignore
                    this.redeemCashMembership.number
                  );
                }) as Enrollment;

                this.formData = { enrollment: linkedEnrollment };
              } else {
                this.formData = {
                  enrollment: this.enrollments[0] as Enrollment,
                };
              }
            } else {
              // enable the form by default
              this.changeFormStatus(true);
            }

            // fetching current cash form prefill
            if (loyaltyProgram && memberships) {
              const { id } = loyaltyProgram;
              const foundMembership = Object.values(memberships).find(
                (membership: Partial<Membership>) =>
                  membership.loyaltyProgramId === id,
              ) as Partial<CashBackToAccountMembership>;
              if (foundMembership) {
                const {
                  firstName,
                  lastName,
                  number: accountNumber,
                  details,
                } = foundMembership;

                this.formDetailData = {
                  enrollmentType: LoyaltyProgramCategory.CashbackToAccount,
                  // @ts-ignore
                  accountFirstName: firstName,
                  // @ts-ignore
                  accountLastName: lastName,
                  // @ts-ignore
                  accountNumber,
                  // @ts-ignore
                  bsbNumber: details.bsbNumber,
                };
              }
            }

            // enable the form
            this.toggleComponentRenderState('content');
          }
        },
      );

    // Subscribe to membership operations

    this.store
      .select(getCreateOrUpdateMembershipIsLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe((createOrUpdateMembershipIsLoading) => {
        this.createOrUpdateMembershipIsLoading =
          createOrUpdateMembershipIsLoading;

        if (createOrUpdateMembershipIsLoading) {
          this.toggleComponentRenderState('submitting');
        }
      });

    this.store
      .select(getCreateOrUpdateMembershipError)
      .pipe(takeUntil(this.destroy$))
      .subscribe((createOrUpdateMembershipError) => {
        this.createOrUpdateMembershipError = createOrUpdateMembershipError;
      });

    // Subscribe to enrollments

    combineLatest([
      this.store.select(getEnrollmentLoading),
      this.store.select(getMembershipLoading),
      this.store.select(getFetchRedeemCashLoyaltyProgramLoading),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        ([enrollmentLoading, membershipLoading, loyaltyProgramLoading]) => {
          if (enrollmentLoading || membershipLoading || loyaltyProgramLoading) {
            this.toggleComponentRenderState('loading');
          }
        },
      );

    this.store
      .select(getEnrollmentError)
      .pipe(takeUntil(this.destroy$))
      .subscribe((enrollmentsError) => {
        this.enrollmentsError = enrollmentsError;
        this.cdRef.markForCheck();
      });

    this.iconRegistryService.registerIcon('rc-enrollments-empty');
    this.membershipAndRedeemCashByCashToAccount$ =
      this.getMembershipAndLoyaltyProgramByCategory(
        LoyaltyProgramCategory.CashbackToAccount,
      );
  }

  toggleComponentRenderState(componentRenderState: ComponentRenderState): void {
    this.componentRenderState = componentRenderState;
    this.cdRef.markForCheck();
  }

  changeFormStatus(status: boolean): void {
    if (this.isBankAccountFormActive !== status) {
      this.isBankAccountFormActive = status;
      this.cdRef.detectChanges();
    }
  }

  get shouldShowButtons(): boolean {
    return ['submitting', 'content'].includes(this.componentRenderState);
  }

  get shouldShowButtonLoading(): boolean {
    return this.createOrUpdateMembershipIsLoading || this.enrollmentsLoading;
  }

  buttonTextKey(): string {
    if (this.shouldShowButtonLoading) {
      return 'redeem_cash_v2.link_account_dialog.loading';
    } else {
      return 'redeem_cash_v2.link_account_dialog.done';
    }
  }

  ngOnDestroy(): void {
    this.store.dispatch(new ClearCreateAndUpdateMembershipErrors());
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  linkAnAccount(): void {
    // inform the children component the form is submitted
    this.isSubmitted$.next(true);
    if (this.isBankAccountFormActive) {
      if (
        (
          this.linkAccountFormGroup.get(
            'linkOtherBankAccountForm',
          ) as AbstractControl
        ).valid
      ) {
        this.submitAccountDetailForm();
      }
    } else {
      this.submitAccountListForm();
    }
  }

  private submitAccountDetailForm(): void {
    const { accountFirstName, accountLastName, accountNumber, bsbNumber } =
      this.linkAccountFormGroup.controls.linkOtherBankAccountForm.value;

    this.membershipAndRedeemCashByCashToAccount$.subscribe(
      ({ membership, loyaltyProgram }) => {
        if (membership && membership.id) {
          this.store.dispatch(
            new UpdateMembership({
              updateMembershipData: {
                type: MembershipType.CashBack,
                membershipId: membership.id,
                membership: {
                  number: accountNumber,
                  firstName: accountFirstName,
                  lastName: accountLastName,
                  details: {
                    bsb_number: bsbNumber,
                  },
                },
              },
            }),
          );
        } else {
          this.store.dispatch(
            new CreateMembership({
              type: MembershipType.CashBack,
              membership: {
                loyalty_program_id: loyaltyProgram.id,
                number: accountNumber,
                firstName: accountFirstName,
                lastName: accountLastName,
                details: {
                  bsb_number: bsbNumber,
                },
              },
            }),
          );
        }
      },
    );
  }

  private submitAccountListForm(): void {
    this.getMembershipAndLoyaltyProgramByCategory(
      this.redeemCashSettingService.defaultEnrollmentListCategory,
    ).subscribe(({ loyaltyProgram, membership }) => {
      if (!loyaltyProgram) {
        return;
      }

      const { groupReferenceId, id } =
        this.linkAccountFormGroup.controls.linkAccountForm.value.enrollment;

      if (membership && membership.id) {
        this.store.dispatch(
          new UpdateMembership({
            updateMembershipData: {
              type: MembershipType.CashBack,
              membershipId: membership.id,
              membership: {
                number: groupReferenceId,
                enrollmentId: id,
              },
            },
          }),
        );
      } else {
        this.store.dispatch(
          new CreateMembership({
            type: MembershipType.CashBack,
            membership: {
              loyalty_program_id: loyaltyProgram.id,
              enrollmentId: id,
              number: groupReferenceId,
            },
          }),
        );
      }
    });
  }

  private checkShouldEnableAccountDetailForm(): void {
    // enable the form if meet both requirements:
    // - shouldAllowRedeemToOtherAccount: true -> has option to show the form
    // - the `preferred` membership has same category with the form `LoyaltyProgramCategory.CashbackToAccount`
    if (
      this.redeemCashSettingService.shouldAllowRedeemToOtherAccount &&
      // @ts-ignore
      this.preferredRedeemCashLoyaltyProgram.category ===
        LoyaltyProgramCategory.CashbackToAccount
    ) {
      this.changeFormStatus(true);
    }
  }

  private getMembershipAndLoyaltyProgramByCategory(
    categoryName: LoyaltyProgramCategory,
  ): Observable<MembershipAndLoyaltyProgramData> {
    // @ts-ignore
    return combineLatest([
      this.store.select(getRedeemCashLoyaltyProgramByCategory(categoryName)),
      this.store.select(getMembershipMemberships),
    ]).pipe(
      takeUntil(this.destroy$),
      map(([loyaltyProgram, memberships]) => {
        if (!loyaltyProgram || !memberships) {
          return {
            membership: null,
            loyaltyProgram: null,
          };
        }
        const foundMembership = Object.values(memberships).find(
          (membership: Partial<Membership>) =>
            membership.loyaltyProgramId === loyaltyProgram.id,
        );

        return {
          loyaltyProgram,
          membership: foundMembership,
        };
      }),
    );
  }
}
